import { useState, useEffect } from 'react'
import Modal from '../common/Modal'
import PhoneInput from '../common/PhoneInput'
import mpesaLogo from '../../assets/mpesa-logo.png'
import { useAuth } from '../../contexts/AuthContext'
import { useMutation } from '@tanstack/react-query'
import { handlePostRequest } from '../../utils/api-handler.util'
import { MERCHANT_NAME } from '../../constants/subscriptionPlans'
import {
  PAYMENT_EXPIRY_KEY,
  PAYMENT_ID_EXPIRY_MILLISECONDS,
  PAYMENT_ID_KEY,
  paymentMethods,
} from '../../constants/payment'
import CurrencyDisplay from '../common/CurrencyDisplay'

const PaymentModal = ({ isOpen, onClose, plan }) => {
  const { currentUser } = useAuth()
  const [phoneNumber, setPhoneNumber] = useState('')
  const [months, setMonths] = useState(1)

  useEffect(() => {
    if (currentUser?.phoneNumber) {
      setPhoneNumber(currentUser.phoneNumber)
    }
  }, [currentUser])

  const paySubscriptionMutation = useMutation({
    mutationFn: async (paymentData) =>
      handlePostRequest(`/${currentUser.userType}s/pay-subscription`, {
        subscriptionId: plan.id,
        paymentMethod: paymentMethods.MPESA,
        phoneNumber: paymentData.phoneNumber,
        intervalCount: paymentData.months,
      }),
    onSuccess: (response) => {
      if (response.success && response.data) {
        localStorage.setItem(PAYMENT_ID_KEY, response.data.paymentId)
        localStorage.setItem(PAYMENT_EXPIRY_KEY, Date.now() + PAYMENT_ID_EXPIRY_MILLISECONDS)
        onClose(true)
      }
    },
    onError: (error) => {
      console.error('Payment initiation failed:', error)
    },
  })

  const calculateTotal = () => {
    if (!plan?.price || !months) return 0
    return plan.price * months
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    await paySubscriptionMutation.mutateAsync({
      phoneNumber,
      months,
    })
  }

  if (!plan) return null

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="flex flex-col items-center relative mx-auto">
        <img src={mpesaLogo} alt="Mpesa" className="w-32 h-auto object-contain mb-4" />

        <h2 className="text-2xl font-bold text-center mb-2">Confirm Payment</h2>
        <p className="text-center text-sm text-gray-600 mb-2 leading-relaxed">
          Complete your subscription payment via M-Pesa. You'll receive a prompt on your phone to
          authorize the transaction.
        </p>
        <p className="text-center text-xs text-gray-500 mb-6">
          Merchant: <strong>{MERCHANT_NAME}</strong>
        </p>

        <form onSubmit={handleSubmit} className="w-full space-y-6">
          <div className="bg-neutral-200-50 rounded-lg p-4 space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Plan</span>
              <span className="font-semibold">{plan.name}</span>
            </div>

            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Monthly Price</span>
              <span className="font-semibold">
                <CurrencyDisplay amount={plan?.price} />
              </span>
            </div>

            <div className="space-y-2">
              <label htmlFor="months" className="block text-sm font-medium text-gray-700">
                Number of Months
              </label>
              <div className="flex items-center space-x-3">
                <button
                  type="button"
                  onClick={() => setMonths(Math.max(1, months - 1))}
                  className="w-8 h-8 rounded-full bg-neutral-200 hover:bg-neutral-200 flex items-center justify-center text-lg font-semibold transition-colors"
                  disabled={months <= 1}
                >
                  −
                </button>
                <input
                  id="months"
                  type="number"
                  min="1"
                  max="12"
                  value={months}
                  onChange={(e) =>
                    setMonths(Math.max(1, Math.min(12, parseInt(e.target.value) || 1)))
                  }
                  className="w-16 text-center border border-gray-300 rounded-md py-1 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  required
                />
                <button
                  type="button"
                  onClick={() => setMonths(Math.min(12, months + 1))}
                  className="w-8 h-8 rounded-full bg-neutral-200 hover:bg-neutral-200 flex items-center justify-center text-lg font-semibold transition-colors"
                  disabled={months >= 12}
                >
                  +
                </button>
              </div>
            </div>

            <div className="border-t pt-3 mt-3">
              <div className="flex justify-between items-center">
                <span className="text-base font-medium text-gray-900">Total Amount</span>
                <span className="text-xl font-bold text-orange-600">
                  <CurrencyDisplay amount={calculateTotal()} />
                </span>
              </div>
              {months > 1 && (
                <p className="text-xs text-gray-500 mt-1">
                  {months} months × <CurrencyDisplay amount={plan?.price} />
                </p>
              )}
            </div>
          </div>

          <div>
            <PhoneInput
              label="M-Pesa Phone Number"
              id="phoneNumber"
              value={phoneNumber}
              onChange={(phone) => setPhoneNumber(phone)}
              required
            />
          </div>

          <div className="space-y-3 pt-4">
            <button
              type="submit"
              disabled={paySubscriptionMutation.isPending}
              className="w-full h-12 bg-gradient-to-r from-[#D17707] to-[#E88F20] text-white text-lg font-medium rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {paySubscriptionMutation.isPending ? (
                <div className="flex items-center justify-center space-x-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Processing...</span>
                </div>
              ) : (
                <>
                  Pay <CurrencyDisplay amount={calculateTotal()} />
                </>
              )}
            </button>

            <button
              type="button"
              onClick={onClose}
              disabled={paySubscriptionMutation.isPending}
              className="w-full h-12 border-2 border-gray-300 text-gray-700 text-lg font-medium rounded-lg hover:border-gray-400 hover:bg-neutral-200-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </Modal>
  )
}

export default PaymentModal
